import Api from "../services/api";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { IApiError, ITransportError } from "../utils/api.interfaces";
import { extend, hashToArray } from "../utils/vanilla";
import { DriveStore } from "../store/reducers";
import { ICachedAddress } from "../store/data/types";
import { DataActions } from "../store/data/actions";
import { getServerDate } from "../utils/storage/date";
import { makeDeviceCache } from "../utils/storage/cache";
import { FeedVersionActions } from "../store/feedVersions/actions";
import { SyncLogger } from "./logger";

export type TSyncCallback = (entityName: string, error?: Error | IApiError | ITransportError, syncLogger?: SyncLogger) => void;

export interface IAddressSearch {
    id: string;
    coordinate: ICoordinate;
}

export interface ILogRecordLookup {
    id: string;
    dateTime: string;
    deviceId: string;
}

export enum FeedTypeKey {
    DVIR_TRAILER = "dvir_trailer",
    DVIR_VEHICLE = "dvir_vehicle",
    HOS = "hos",
    MALFUNCTION = "malfunctions",
    RECIPIENT_TEXT_MESSAGE = "RecipientTextMessage",
    DEVICE_TEXT_MESSAGE = "DeviceTextMessage",
    USER_TEXT_MESSAGE = "UserTextMessage",
    SHIPMENT = "shipment",
    TRAILER_ATTACHMENT = "trailer_attachment",
    USER_HOS_RULESET = "UserHosRuleSet"
}

export let noEntityIds = ["NoDeviceId", "NoTrailerId", "NoUserId"];

let findStringInException = (exceptionType: string, strExpression: RegExp, errorObject: IApiError): string | undefined => {
    if (errorObject && errorObject.data && errorObject.data.type === exceptionType) {
        let messageMatch = errorObject.message.match(strExpression);
        return messageMatch && messageMatch[1] ? messageMatch[1] : undefined;
    }
    return "";
};

/**
 * Gets the feed version for a key
 * @param api The API object
 * @param store The DriveStore object
 * @param typeKey The feed type
 * @param id An optional id for a singular asset of this type (eg. DVIR logs for trailer "b2" vs. logs for trailer "b3")
 * @return The current feed version. Null if not present.
 */
export function getFeedVersion(api: Api, store: DriveStore, typeKey: FeedTypeKey, id?: string) {
    let apiUser = api.getUser();
    let apiUserId = apiUser ? apiUser.id : undefined;
    if (!apiUserId) {
        return null;
    }

    let feedVersions: IIdDictionary<string | IIdDictionary<string>> = extend(true, {}, store.getState().feedVersions[apiUserId] || {}) as IIdDictionary<string | IIdDictionary<string>>;
    let feedVersionForType = feedVersions[typeKey] || null;
    if (!feedVersionForType) {
        return null;
    } else if (id) {
        return (feedVersionForType as IIdDictionary<string>)[id] || null;
    }

    return feedVersionForType as string || null;
}

/**
 * Sets a feed version for a key
 * @param api The API object
 * @param store The DriveStore object
 * @param typeKey The feed type
 * @param version The version to set
 * @param id An optional id for a singular asset of this type (eg. DVIR logs for trailer "b2" vs. logs for trailer "b3")
 * @param syncLogger The SyncLogger object
 */
export function setFeedVersion(api: Api, store: DriveStore, typeKey: FeedTypeKey, version: string, id?: string, syncLogger?: SyncLogger): void {
    let apiUser = api.getUser();
    let apiUserId = apiUser ? apiUser.id : undefined;
    syncLogger && syncLogger.setFeedVersion(`${ typeKey }${ id ? `_${ id }` : "" }`, version);

    if (apiUserId && typeKey && version) {
        let feedVersions = extend(true, {}, store.getState().feedVersions[apiUserId] || {}) as IIdDictionary<string | IIdDictionary<string>>;
        if (id) {
            feedVersions[typeKey] = feedVersions[typeKey] || {};
            feedVersions[typeKey][id] = version;
        } else {
            feedVersions[typeKey] = version;
        }
        store.dispatch(FeedVersionActions.setFeedVersions({ userId: apiUserId, feedVersions: feedVersions }));
    }
}

/**
 * Removes a stored feed version so the next sync for that type restarts from a full Get.
 * No-op when nothing is stored, so it is safe to call on every sync cycle.
 */
export function clearFeedVersion(api: Api, store: DriveStore, typeKey: FeedTypeKey, id?: string): void {
    let apiUser = api.getUser();
    let apiUserId = apiUser ? apiUser.id : undefined;
    if (!apiUserId || !typeKey) {
        return;
    }

    let storedVersions = store.getState().feedVersions[apiUserId];
    if (!storedVersions || storedVersions[typeKey] === undefined) {
        return;
    }

    let feedVersions = extend(true, {}, storedVersions) as IIdDictionary<string | IIdDictionary<string>>;
    if (id) {
        let versionsForType = feedVersions[typeKey] as IIdDictionary<string>;
        if (!versionsForType || versionsForType[id] === undefined) {
            return;
        }
        delete versionsForType[id];
    } else {
        delete feedVersions[typeKey];
    }
    store.dispatch(FeedVersionActions.setFeedVersions({ userId: apiUserId, feedVersions: feedVersions }));
}


/**
 * Removes feed version for a key
 * @param api The API object
 * @param store The DriveStore object
 * @param typeKey The feed type
 * @param id An optional id for a singular asset of this type (eg. DVIR logs for trailer "b2" vs. logs for trailer "b3")
 */
export function removeFeedVersion(api: Api, store: DriveStore, typeKey: FeedTypeKey, id?: string): void {
    let apiUser = api.getUser();
    let apiUserId = apiUser ? apiUser.id : undefined;
    if (apiUserId && typeKey) {
        let feedVersions = extend(true, {}, store.getState().feedVersions[apiUserId] || {}) as IIdDictionary<string | IIdDictionary<string>>;
        if (id && feedVersions[typeKey]) {
            delete feedVersions[typeKey][id];
        } else {
            delete feedVersions[typeKey];
        }
        store.dispatch(FeedVersionActions.setFeedVersions({ userId: apiUserId, feedVersions: feedVersions }));
    }
}

/**
 * Merge models. Sort of like _.extend(), but doesn't overwrite existing entity properties with nulls, undefineds, or "NoUserId", "NoDriverId", etc.
 * @param obj Multiple models/objects to merge
 * @return A cloned model/object that merged all arguments
 */
export function merge<T> (...obj): T {
    let entity: T = obj[0] ? extend<{}, T>({}, obj[0]) : {} as T;
    let srcProp;

    Array.prototype.slice.call(obj, 1).forEach(function (source) {
        if (source) {
            for (let prop in source) {
                if (source.hasOwnProperty(prop)) {
                    srcProp = source[prop];
                    if (srcProp !== null && typeof (srcProp) !== "undefined" && noEntityIds.indexOf(srcProp) === -1) {
                        entity[prop] = source[prop];
                    }
                }
            }
        }
    });
    return entity;
}

/**
 * Gets the foreign key constraint (i.e. FK_Something_Else)
 * @param errorObject The error object from API
 * @return The constraint name or null
 */
export function getForeignKeyConstraint(errorObject: IApiError) {
    return findStringInException("RelationViolatedException", /(FK_[\w\d-]+)/i, errorObject);
}

export function getForeignKeyErrorObjectId(errorObject: IApiError) {
    return findStringInException("RelationViolatedException", /(ID=[\w\d-]+)/i, errorObject)?.substring(3);
}

export function formatCallForLogger(typeName: string, calls: any[]): string {
    let entities = "";
    if (calls && calls.length > 0) {
        entities = calls.map(call => {
            let operation = call[0];
            let entity = call[1].entity;
            let props: string[] = [];

            if (entity.id) {
                props.push(`id:${ entity.id }`);
            }
            if (entity.dateTime || entity.sent) {
                props.push(`date:${ entity.sent ? entity.sent : entity.dateTime }`);
            }
            if (entity.duration) {
                props.push(`duration:${ entity.duration }`);
            }
            if (entity.status) {
                props.push(`status:${ entity.status }`);
            }
            if (entity.trailer && entity.trailer.id) {
                props.push(`trailer:${ entity.trailer.id }`);
            }
            if (entity.device && entity.device.id) {
                props.push(`device:${ entity.device.id }`);
            }
            if (entity.origin) {
                props.push(`origin:${ entity.origin }`);
            }
            if (entity.text) {
                props.push(`text:${ entity.text }`);
            }
            if (entity.group?.id) {
                props.push(`group:${ entity.group?.id }`);
            }
            if (entity.from) {
                props.push(`from:${ entity.from }`);
            }
            if (entity.to) {
                props.push(`to:${ entity.to }`);
            }
            if (entity._syncCounter) {
                props.push(`syncCounter:${ entity._syncCounter }`);
            }
            return `${ operation }\{${ props.join(",") }\}`;
        }).join(",");
    }
    return `${ typeName } push => ${ entities }`;
}

/**
 * Helper method used by populateAddressesRedux to call the GetAddresses api for a given set of
 * coordinates.
 * @param api The API
 * @param store The Drive data store
 * @param entities The entities that need address populated
 * @param isDvir Whether or not we want to obtain DVIR formatted addresses (i.e. 123 Main St East)
 * @returns The promise for the address lookups
 **/
function fetchAddresses(api: Api, store: DriveStore, entities: any[], isDvir: boolean) {
    let searches = Array.isArray(entities) ? entities : [entities];

    return api.callPromise("GetAddresses", { coordinates: searches.map(e => e.coordinate), hosAddresses: !isDvir })
        .then((results: any[]) => {
            let addressesToUpdate: { [coordinates: string]: ICachedAddress } = {};
            if (results && results.length) {
                let serverDate = getServerDate().toISOString();
                for (let i = 0; i < results.length; i++) {
                    if (Object.keys(results[i]).length >= 1) {

                        // If we have the item cached already, we will want to retrieve it and add the newly requested address format, as well as reset the fetch date/time.
                        let cacheKey = `${ searches[i].coordinate.x.toFixed(4) }__${ searches[i].coordinate.y.toFixed(4) }`;
                        let cachedAddress = {...store.getState().data.addressCache[cacheKey]};
                        cachedAddress.logDate = serverDate;

                        if (isDvir) {
                            cachedAddress.formattedAddress = results[i].formattedAddress;
                            cachedAddress.country = results[i].country;
                            cachedAddress.region = results[i].region;
                        } else {
                            cachedAddress.formattedHosAddress = results[i].formattedAddress;
                        }

                        addressesToUpdate[cacheKey] = cachedAddress as ICachedAddress;
                    }
                }
                store.dispatch(DataActions.setAddressCache(addressesToUpdate));
            }
            return addressesToUpdate;
        });
}

/**
 * Gets and stores addresses for entities (using redux! =])
 * @param api The API
 * @param store The Drive data store
 * @param entities The entities that need address populated
 * @param isDvir Whether or not we want to obtain DVIR formatted addressses (i.e. 123 Main St East)
 * @return The promise for the address lookup
 * */
export function populateAddressesRedux(api: Api, store: DriveStore, entities: IAddressSearch[], isDvir: boolean) {
    let existing: DeepReadonly<ICachedAddress>[] = [];
    let missing: IAddressSearch[] = [];
    let addressCache = store.getState().data.addressCache;

    entities.forEach(entity => {
        let cached = addressCache[`${ entity.coordinate.x.toFixed(4) }__${ entity.coordinate.y.toFixed(4) }`];

        if (cached) {
            // If the lookup succeeded and the correct format of address is available, we don't need to look it up.
            if (isDvir ? cached.formattedAddress : cached.formattedHosAddress) {
                existing.push(cached);
            } else {
                missing.push(entity);
            }
        } else {
            missing.push(entity);
        }
    });

    if (missing.length > 0) {
        return Promise.resolve(fetchAddresses(api, store, missing, isDvir).then(_ => {
            let updatedAddressCache = store.getState().data.addressCache;
            return entities.map(entity => {
                return updatedAddressCache[`${ entity.coordinate.x.toFixed(4) }__${ entity.coordinate.y.toFixed(4) }`];
            });
        }));
    } else {
        return Promise.resolve(existing);
    }
}

/**
 * Gets addresses for existing entities (using redux! =])
 * @param store The Drive data store
 * @param entities The entities that need address populated
 * @return The promise for the address lookup
 * */
export function populateDownloadedAddressesRedux(store: DriveStore, entities: IAddressSearch[]) {
    let addressCache = store.getState().data.addressCache;

    let x = entities.map(e => {
        let cached = addressCache[`${ e.coordinate.x.toFixed(4) }__${ e.coordinate.y.toFixed(4) }`];

        if (cached) {
            return cached;
        }

        return <ICachedAddress>{};
    });

    return x;
}

export function hasKnownDevice(log: IDeviceEntity): boolean {
    return !!(log.device && log.device.id && log.device.id !== "NoDeviceId");
}

/**
 * Gets rulesets (using redux! =])
 * @param store The Drive data store
 * @return The promise for the rulesets lookup
 * */
export function populateDownloadedRuleSetsRedux(store: DriveStore) {
    let ruleSets = store.getState().data.ruleSets;
    return Promise.resolve(ruleSets);
}

/**
 * Gets and stores vehicles
 * @param activeVehicles The list of active vehicles to look up
 * @param currentUser The current user
 * @param api The api
 * @return The promise for the device lookup
 * */
export function preloadVehicles(activeVehicles: IIdDictionary<IIdEntity>, currentUser: IUser | null, api: Api, store: DriveStore) {
    let apiUserId = currentUser ? currentUser.id : undefined;
    let vehicles = hashToArray<{ id: string }>(activeVehicles).map(vehicle => ({ id: vehicle.id }));

    return makeDeviceCache(store).getOrFetch(api, vehicles, apiUserId);
}

/**
 * Gets LogRecords
 * @param logRecordLookups The list of coordinates to look up
 * @param api The api
 * @return The promise for the log records lookup
 * */
export function getLogRecords(logRecordLookups: ILogRecordLookup[], api: Api) {
    return new Promise((resolve, reject) => {
        if (logRecordLookups.length === 0) {
            resolve([]);
            return;
        }

        api.multiCall(logRecordLookups.map((log) => {
            return ["Get", {
                typeName: "LogRecord",
                search: {
                    deviceSearch: {
                        id: log.deviceId
                    },
                    fromDate: log.dateTime,
                    toDate: log.dateTime
                }
            }];
        }), resolve, reject);
    });
}

export function createTimeoutSignal(timeoutMessage: string, timeoutDuration: number) {
    return new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error(timeoutMessage));
        }, timeoutDuration);
    });
}